/**
 * Discount engine fixtures — DISCOUNT_SCHEDULES + CUSTOMER_DISCOUNTS.
 *
 * Used in mock-mode (NEXT_PUBLIC_USE_MOCKS=true / VITE_USE_MOCKS=true) and in
 * unit-tests. Shapes are validated against the zod row-schemas on module load.
 *
 * Duration fields (active_duration / inactive_duration) are INTEGER SECONDS:
 *   15 days = 15 * 86400 = 1_296_000 s
 *    5 days =  5 * 86400 =   432_000 s
 *
 * site_code 'pl' matches the primary SITES fixture (wandero.pl).
 */

import {
  DiscountScheduleRowSchema,
  CustomerDiscountRowSchema,
  type DiscountScheduleRow,
  type CustomerDiscountRow,
} from '../schemas/discount.js';

// ── DISCOUNT_SCHEDULES ────────────────────────────────────────────────────────
//
// One active cyclic schedule for 'pl':
//   start_at    2026-01-01 (well in the past — guaranteed ON at test/demo time)
//   active      15 days   (1_296_000 s)
//   inactive     5 days   (  432_000 s)
//   → cycleLen  20 days
//   discount    15% off
//
// Invariant check (spec §5): active_duration (1_296_000) > inactive_duration (432_000) ✓
//
// A second disabled schedule is included so the resolver/mock can verify that
// disabled rows are never selected.

export const DISCOUNT_SCHEDULES: readonly DiscountScheduleRow[] = [
  {
    id: 1,
    site_code: 'pl',
    start_at: '2026-01-01T00:00:00.000Z',
    // 15 days in seconds
    active_duration: 15 * 86400,
    // 5 days in seconds
    inactive_duration: 5 * 86400,
    end_at: null,
    discount_type: 'pct',
    discount_value: '15',
    enabled: true,
    note: 'Seasonal 15% promo — repeating 15-day ON / 5-day OFF cycle',
    created_at: '2026-01-01T00:00:00.000Z',
    updated_at: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 2,
    site_code: 'pl',
    start_at: '2026-06-01T00:00:00.000Z',
    // 30 days in seconds
    active_duration: 30 * 86400,
    // 10 days in seconds
    inactive_duration: 10 * 86400,
    end_at: '2026-12-31T23:59:59.000Z',
    discount_type: 'pct',
    discount_value: '20',
    enabled: false,
    note: 'Draft summer sale — disabled, not active in mock',
    created_at: '2026-06-01T00:00:00.000Z',
    updated_at: '2026-06-01T00:00:00.000Z',
  },
];

// Validate at module load time — fail loudly during dev if shapes drift.
DISCOUNT_SCHEDULES.forEach((row) => DiscountScheduleRowSchema.parse(row));

// ── CUSTOMER_DISCOUNTS ────────────────────────────────────────────────────────
//
// Two segment rules for 'pl':
//   new       → 10% welcome discount (email_verified, 0 paid orders)
//   returning → 8% loyalty discount  (>= 1 paid order)
//
// UNIQUE (site_code, segment) matches the DB constraint — one rule per segment.

export const CUSTOMER_DISCOUNTS: readonly CustomerDiscountRow[] = [
  {
    id: 1,
    site_code: 'pl',
    segment: 'new',
    discount_type: 'pct',
    discount_value: '10',
    min_paid_orders: 0,
    enabled: true,
    note: 'Welcome discount for new verified customers',
    created_at: '2026-01-01T00:00:00.000Z',
    updated_at: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 2,
    site_code: 'pl',
    segment: 'returning',
    discount_type: 'pct',
    discount_value: '8',
    min_paid_orders: 1,
    enabled: true,
    note: 'Loyalty discount for returning customers',
    created_at: '2026-01-01T00:00:00.000Z',
    updated_at: '2026-01-01T00:00:00.000Z',
  },
];

// Validate at module load time.
CUSTOMER_DISCOUNTS.forEach((row) => CustomerDiscountRowSchema.parse(row));
